import AtgGifLoader from '@common/AtgGifLoader';
import { Box } from '@atgfr8/atg-ui-tailwind';
import { AutoComplete } from '@components';
import SectionCard from '@mobileComponents/SectionCard';
import {
  fetchCustomers,
  getCustomerData,
  searchCustomers,
} from '@mobileStore/customer';
import { useNavigate } from '@solidjs/router';
import { CustomerAutoCompleteObject } from '@store/orders/types';
import { formatAmount } from '@utils/utils';
import { throttle } from 'lodash';
import { createSignal, For, onMount, Show } from 'solid-js';
import { dashboardStore } from '@mobileStore/global/store';
import { getAlignmentClassMobile } from '@mobileUtils/helper';

import cls from './classes';

const CustomerSearchItem = (props: { item: CustomerAutoCompleteObject }) => (
  <div class="w-full px-3 py-1">
    <div class="font-bold text-gray-900 text-sm">{props.item.name}</div>
    <div class="flex flex-col text-xs text-gray-500">
      <Show when={props.item.accountManager}>
        <div>
          Account Manager{' '}
          <span class="font-medium text-gray-700">
            {props.item.accountManager}
          </span>
        </div>
      </Show>
      <Show when={props.item.office}>
        <div>
          Office{' '}
          <span class="font-medium text-gray-700">{props.item.office}</span>
        </div>
      </Show>
    </div>
  </div>
);

export type CustomerDetails = {
  id: number;
  name: string;
  status: string;
  subStatus: string | null;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  creditLimit: number;
  creditAvailable: number;
  location: string;
  salesManager: string;
  salesManagerId: number;
  accountManager: string;
  accountManagerId: number;
  office: string;
  officeId: number;
  verified: boolean;
  createdDate: string;
};

const Customer = () => {
  const [customers, setCustomers] = createSignal<CustomerDetails[]>([]);
  const [customerLength, setCustomerLength] = createSignal(0);
  const [inputValue, setInputValue] = createSignal('');
  const [isLoading, setIsLoading] = createSignal(true);
  const [pageNumber, setPageNumber] = createSignal(1);
  const [hasMore, setHasMore] = createSignal(true);
  const navigate = useNavigate();
  const [scrollContainer, setScrollContainer] = createSignal<HTMLDivElement>();

  const fetchCustomerList = async (page = 1) => {
    let newData: CustomerDetails[] = [];
    let totalCount = 0;
    if (inputValue()) {
      const resp = await searchCustomers(
        inputValue().toLowerCase(),
        (page - 1) * 40,
      );
      newData = (resp?.data ?? []) as unknown as CustomerDetails[];
      totalCount = resp?.totalCount || 0;
    } else {
      const resp = await fetchCustomers({
        filters: [],
        sort: {
          field: 'createdDate',
          direction: 'desc',
        },
        pageNumber: page,
        pageSize: 25,
      });
      newData = (resp?.value.data ?? []) as unknown as CustomerDetails[];
      totalCount = resp?.value.totalCount || 0;
    }

    if (!newData.length) {
      setHasMore(false);
      if (page === 1) {
        setCustomers([]);
        setCustomerLength(0);
      }
      return;
    }

    if (page > 1) {
      setCustomers([...customers(), ...newData]);
    } else {
      setCustomers(newData);
    }

    if (inputValue()) {
      setCustomerLength(totalCount);
      const loaded = customers().length;
      if (loaded >= totalCount || newData.length < 40) {
        setHasMore(false);
      }
    } else {
      setCustomerLength(totalCount);
      const loaded = customers().length;
      if (loaded >= totalCount || newData.length < 25) {
        setHasMore(false);
      }
    }
  };

  onMount(async () => {
    setIsLoading(true);
    setPageNumber(1);
    setHasMore(true);
    await fetchCustomerList(1);
    setIsLoading(false);
  });

  const handleScroll = throttle(async () => {
    const el = scrollContainer();
    if (isLoading() || !hasMore()) return;

    if (!!el) {
      const bottomReached =
        el.scrollTop + el.clientHeight >= el.scrollHeight - 20;

      if (bottomReached) {
        setIsLoading(true);
        const nextPage = pageNumber() + 1;
        setPageNumber(nextPage);
        await fetchCustomerList(nextPage);
        setIsLoading(false);
      }
    }
  }, 200);

  const handleSearch = async () => {
    try {
      setIsLoading(true);
      setPageNumber(1);
      setHasMore(true);
      await fetchCustomerList(1);
    } finally {
      setIsLoading(false);
      queueMicrotask(() => scrollContainer()?.scrollTo({ top: 0 }));
    }
  };

  const handleClear = async () => {
    try {
      setIsLoading(true);
      setInputValue('');
      setPageNumber(1);
      setHasMore(true);
      await fetchCustomerList(1);
    } finally {
      setIsLoading(false);
      queueMicrotask(() => scrollContainer()?.scrollTo({ top: 0 }));
    }
  };

  const handleSelectCustomer = async (item: CustomerAutoCompleteObject) => {
    setInputValue(item.name);
    await handleSearch();
  };

  const handleCustomerInfo = async (customerId: string) => {
    try {
      setIsLoading(true);
      await getCustomerData(customerId);
      navigate(`/customer/details/${customerId}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box class="flex flex-col bg-[#e1ecf2]">
      <Show when={isLoading()}>
        <AtgGifLoader />
      </Show>
      <div
        class={`shrink-0 sticky bg-white border-t border-[#dcdcdc] px-2 py-2 z-10 ${getAlignmentClassMobile(dashboardStore.isSearchGlobalOpen, dashboardStore.globalSearchHasError)}`}
        style={{
          'border-bottom-left-radius': '16px',
          'border-bottom-right-radius': '16px',
        }}
      >
        <div class="[&_.MuiInputLabel-root]:text-sm!">
          <AutoComplete<CustomerAutoCompleteObject>
            id="customer-list-search"
            endpoint="customer/autocomplete/?term"
            label="Search Customers"
            dataTestId="customer-list-search-input"
            responseFieldName="name"
            disabled={isLoading()}
            value={inputValue()}
            minCharacters={0}
            renderItem={(item) => <CustomerSearchItem item={item} />}
            onItemSelect={(item) => void handleSelectCustomer(item)}
            onClearValue={() => void handleClear()}
          />
        </div>
        <Show when={customerLength() > 0}>
          <div class="mt-2 text-grey text-[12px]">
            {customerLength()} Results
          </div>
        </Show>
      </div>
      <Box
        ref={setScrollContainer}
        on:scroll={handleScroll}
        class="flex-1 overflow-auto bg-[#e1ecf2]"
      >
        <Box class={cls.cardListContainer}>
          <Show when={!isLoading() && customers().length === 0}>
            <Box class={cls.emptyContainer}>
              <p class={cls.emptyText}>No customers found</p>
            </Box>
          </Show>
          <For each={customers()}>
            {(items) => (
              <SectionCard
                title={<p class={cls.startTitle}>{items.name}</p>}
                class="mb-2!"
              >
                <div
                  class={`${items.status !== 'Customer' ? cls.inactiveContent : cls.activeContent} rounded-b-md p-2`}
                >
                  <div
                    class={cls.cardContent}
                    onClick={() => handleCustomerInfo(items.id.toString())}
                  >
                    <div class={cls.row}>
                      <div class={cls.key}>Status</div>
                      <div class={cls.value}>
                        {items.subStatus
                          ? `${items.status} - ${items.subStatus}`
                          : items.status}
                      </div>
                    </div>
                    <div class={cls.row}>
                      <div class={cls.key}>Credit</div>
                      <div class={cls.value}>
                        {formatAmount(items.creditLimit)}
                      </div>
                    </div>
                    <div class={cls.row}>
                      <div class={cls.key}>Account Manager</div>
                      <div class={cls.value}>{items.accountManager}</div>
                    </div>
                    <div class={cls.row}>
                      <div class={cls.key}>Sales Manager</div>
                      <div class={cls.value}>{items.salesManager}</div>
                    </div>
                  </div>
                </div>
              </SectionCard>
            )}
          </For>
        </Box>
      </Box>
    </Box>
  );
};

export default Customer;
